import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EEmit, ESocketEvents } from '@enums/api.enum';
import { TripStatus } from '@enums/trips.enum';
import type { RootState } from '@store/store';
import {
	removeDriverFromTrip,
	removeDriverFromTripsList,
	removeTrip,
	removeVehicleFromTrip,
	removeVehicleFromTripsList,
	updateCurrentTripStatus,
	updateTrip,
	type Driver,
	type Trip,
	type Vehicle,
} from '@store/trips/tripsSlice';
import { getSocket } from '@utils/socket';

import useApi from './useApi';

export type SocketTripStatusChange = {
	ids: number[];
	newStatus: TripStatus;
};

export type SocketTripUpdate = {
	ids: number[];
	driverId?: Driver['id'] | null;
	vehicleId?: Vehicle['id'] | null;
	assignmentCleared?: boolean;
};

export default function useTripSocket() {
	const data = useSelector((state: RootState) => state.trips.data);

	const dataRef = useRef(data);
	const socket = getSocket();
	const { getDriverAndVehicle, updateSingleTrip } = useApi();
	const dispatch = useDispatch();

	const handleChangeStatus = async (ids: number[], newStatus: TripStatus) => {
		dispatch(updateCurrentTripStatus(newStatus));
		ids.forEach((id) => {
			const index = dataRef.current.findIndex((trip) => trip.id === id);
			if (index > -1) {
				if (newStatus === TripStatus.Done) {
					dispatch(removeTrip(id));
				} else if (newStatus === TripStatus.Canceled) {
					updateSingleTrip(id);
				} else {
					const updatedTrip = { ...dataRef.current[index], status: newStatus };
					dispatch(updateTrip(updatedTrip));
				}
			}
		});
	};

	const handleTripUpdateDriver = (ids: number[], driverId?: Driver['id'] | null, vehicleId?: Vehicle['id'] | null) => {
		if (!driverId && !vehicleId) {
			return;
		}

		ids.forEach((id) => {
			getDriverAndVehicle({
				id,
				driverId,
				vehicleId,
			});
		});
	};

	const handleAssignmentCleared = (ids: number[], driverId?: Driver['id'] | null, vehicleId?: Vehicle['id'] | null) => {
		if (driverId === undefined && vehicleId === undefined) {
			return;
		}
		if (driverId === null) {
			dispatch(removeDriverFromTrip());
		}
		if (vehicleId === null) {
			dispatch(removeVehicleFromTrip());
		}

		ids.forEach((id) => {
			const index = dataRef.current.findIndex((trip) => trip.id === id);

			if (index > -1) {
				if (driverId === null) {
					dispatch(removeDriverFromTripsList({ id }));
				}
				if (vehicleId === null) {
					dispatch(removeVehicleFromTripsList({ id }));
				}
			}
		});
	};

	const connectTripsSocket = () => {
		if (!socket) {
			return null;
		}

		socket.connect();
	};

	const handleTripsSocketEvents = (socketTripsIds: Trip['id'][]) => {
		if (!socket || !socket.connected) {
			return null;
		}

		socket.emit(EEmit.joinRooms, {
			trips: socketTripsIds,
		});

		socket.off(ESocketEvents.tripStatusChange);
		socket.on(ESocketEvents.tripStatusChange, ({ ids, newStatus }: SocketTripStatusChange) => {
			handleChangeStatus(ids, newStatus);
		});

		socket.off(ESocketEvents.tripUpdate);
		socket.on(ESocketEvents.tripUpdate, ({ ids, driverId, vehicleId, assignmentCleared }: SocketTripUpdate) => {
			if (assignmentCleared) {
				handleAssignmentCleared(ids, driverId, vehicleId);
			} else {
				handleTripUpdateDriver(ids, driverId, vehicleId);
			}
		});
	};

	const disconnectTripsSocket = () => {
		if (!socket) {
			return null;
		}
		socket.off(); // remove all listeners
		socket.disconnect();
	};

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	return {
		socket,
		connectTripsSocket,
		handleTripsSocketEvents,
		disconnectTripsSocket,
	};
}
